import { PageTemplate, Button, Wrapper } from "../styles";
import { useParams } from "react-router-dom";
import { DataContext } from "../DataContext";
import { useContext, useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import toast from "react-hot-toast";
import { getCatalogProductBySlug } from "../api/product";
import { getSizeOptions, getStockBySize, getMaxQuantity, getQuantityOptions, isCompletelyOutOfStock, getPrimaryImage, sortProductImages } from "../utils";
import { Modal } from "react-bootstrap";
import SizeChart from "../components/SizeChart";
import { useNavigate } from "react-router-dom";

const findPricingProductByProductId = (products, productId) => {
  return products.find((product) => product.productId === productId);
};

const getSizeLabel = (selectedSize) => {
  const sizeMap = {
    XS: "Extra Small",
    S: "Small",
    M: "Medium",
    L: "Large",
    XL: "Extra Large",
    XXL: "Double XL",
  };

  return sizeMap[selectedSize] || "";
};

const getDisplayPrice = (unitAmountInCents, style) => {
  if (typeof unitAmountInCents !== "number") return "";

  const salePrice = unitAmountInCents / 100;

  if (style?.discounted) {
    const discount = style.discountAmount || 0;
    const retailPrice = salePrice + discount;

    return (
      <>
        ${salePrice.toFixed(2)}
        <span className="retail-price">${retailPrice.toFixed(2)}</span>
      </>
    );
  }

  return `$${salePrice.toFixed(2)}`;
};

const findPriceByLookupKey = (prices = [], lookupKey) => {
  return prices.find((price) => price.lookup_key === lookupKey);
};

const getStyleOptions = (pricingProduct, catalogProduct) => {
  if (!pricingProduct?.prices || !catalogProduct?.styles) return [];

  const defaultLookupKey = catalogProduct.defaultLookupKey;

  return catalogProduct.styles
    .map((style) => {
      console.log("bobtest", pricingProduct.prices, style.lookupKey);
      const matchingPrice = findPriceByLookupKey(pricingProduct.prices, style.lookupKey);

      if (!matchingPrice) return null;

      return {
        id: matchingPrice.id,
        lookupKey: style.lookupKey,
        unitAmount: matchingPrice.unit_amount,
        color: style.colorName,
        hexCode: style.hexCode,
        discounted: style.discounted || false,
        discountAmount: style.discountAmount || 0,
        inventory: style.inventory || {},
        images: style.images || [],
        isDefault: style.lookupKey === defaultLookupKey,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
};

const Product = () => {
  const { slug } = useParams();
  const { addToCart, pricingProducts, productsInCart } = useContext(DataContext);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [catalogProduct, setCatalogProduct] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSizeChartModal, setShowSizeChartModal] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const touchStartX = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadCatalogProduct = async () => {
      if (!slug) return;

      try {
        setCatalogProduct(null);
        setSelectedOption(null);
        setSelectedSize(null);
        setQuantity(1);

        const token = isAuthenticated ? await getAccessTokenSilently() : null;
        const response = await getCatalogProductBySlug(slug, token);
        setCatalogProduct(response.product);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load catalog product");
      }
    };

    loadCatalogProduct();
  }, [slug, getAccessTokenSilently, isAuthenticated]);

  const product = useMemo(() => {
    if (!catalogProduct?.productId) return null;

    return findPricingProductByProductId(pricingProducts, catalogProduct.productId);
  }, [pricingProducts, catalogProduct]);

  const styleOptions = useMemo(() => {
    if (!product || !catalogProduct) return [];
    return getStyleOptions(product, catalogProduct);
  }, [product, catalogProduct]);

  const sizeOptions = useMemo(() => {
    return getSizeOptions(selectedOption?.inventory || {});
  }, [selectedOption]);

  const outOfStock = useMemo(() => {
    return isCompletelyOutOfStock(selectedOption?.inventory || {});
  }, [selectedOption]);

  const selectedSizeStock = useMemo(() => {
    if (!selectedSize || !selectedOption) return 0;
    return getStockBySize(selectedOption.inventory, selectedSize);
  }, [selectedSize, selectedOption]);

  const totalStockForSelectedSize = useMemo(() => {
    return Number(selectedSizeStock || 0);
  }, [selectedSizeStock]);

  const quantityAlreadyInCart = useMemo(() => {
    if (!selectedOption?.lookupKey || !selectedSize) return 0;

    return (productsInCart || [])
      .filter((item) => item.lookupKey === selectedOption.lookupKey && item.size === selectedSize)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }, [productsInCart, selectedOption?.lookupKey, selectedSize]);

  const remainingStock = useMemo(() => {
    return Math.max(totalStockForSelectedSize - quantityAlreadyInCart, 0);
  }, [totalStockForSelectedSize, quantityAlreadyInCart]);

  const isSoldOut = useMemo(() => {
    return !!selectedSize && totalStockForSelectedSize <= 0;
  }, [selectedSize, totalStockForSelectedSize]);

  const isMaxInBag = useMemo(() => {
    return !!selectedSize && totalStockForSelectedSize > 0 && remainingStock <= 0;
  }, [selectedSize, totalStockForSelectedSize, remainingStock]);

  const maxQuantity = useMemo(() => {
    if (!selectedSize || remainingStock <= 0) return 1;
    return getMaxQuantity(remainingStock, 5);
  }, [selectedSize, remainingStock]);

  const quantityOptions = useMemo(() => {
    if (!selectedSize || remainingStock <= 0) return [1];
    return getQuantityOptions(remainingStock, 5);
  }, [selectedSize, remainingStock]);

  useEffect(() => {
    if (!styleOptions.length) return;

    const defaultOption = styleOptions.find((styleOption) => styleOption.isDefault) ?? styleOptions[0];
    setSelectedOption(defaultOption);
  }, [styleOptions]);

  useEffect(() => {
    if (quantity > maxQuantity) {
      setQuantity(maxQuantity);
    }
  }, [quantity, maxQuantity]);

  const chooseColor = (priceOption) => {
    setSelectedSize(null);
    setQuantity(1);
    setActiveImageIndex(0);
    setSelectedOption(priceOption);
  };

  const chooseSize = (size) => {
    setSelectedSize(size);
    setQuantity(1);
  };

  const handleAddToBag = async () => {
    if (!selectedSize || !selectedOption || !product || remainingStock === 0) return;

    const selectedStyle = catalogProduct.styles?.find((style) => style.lookupKey === selectedOption.lookupKey);

    const thumbnail = getPrimaryImage(selectedStyle)?.url || "";

    const payload = {
      productId: product.productId,
      productName: catalogProduct.productName,
      priceId: selectedOption.id,
      productUrlSlug: catalogProduct.productUrlSlug,
      design: selectedOption.color,
      lookupKey: selectedOption.lookupKey,
      size: selectedSize,
      quantity,
      productCost: selectedOption.unitAmount,
      availableStock: selectedSizeStock,
      thumbnail,
    };

    try {
      const result = await addToCart(payload);

      if (!result?.success) {
        toast.error(result?.message);
      } else {
        toast((t) => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Success</span>
            <Button
              style={{ marginLeft: "10px", fontWeight: "bold" }}
              onClick={() => {
                toast.dismiss(t.id);
                navigate("/bag");
              }}
            >
              View Shopping Bag
            </Button>
          </div>
        ));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };

  const addToBagLabel = !selectedSize
    ? "SELECT A SIZE"
    : isSoldOut
      ? "OUT OF STOCK"
      : isMaxInBag
        ? "MAX QUANTITY IN BAG"
        : "ADD TO SHOPPING BAG";

  const disableAddToBag = !selectedSize || isSoldOut || isMaxInBag;

  const isDiscounted = selectedOption?.discounted;

  const [zoomedImage, setZoomedImage] = useState(null);

  const selectedStyle = useMemo(() => {
    return catalogProduct?.styles?.find((style) => style.lookupKey === selectedOption?.lookupKey);
  }, [catalogProduct, selectedOption?.lookupKey]);

  const productImages = useMemo(() => {
    return sortProductImages(selectedStyle?.images || []);
  }, [selectedStyle]);

  useEffect(() => {
    if (activeImageIndex >= productImages.length) {
      setActiveImageIndex(0);
    }
  }, [activeImageIndex, productImages.length]);

  if (!product || !catalogProduct || !selectedOption) {
    return null;
  }

  const handleProductImageMouseMove = (event) => {
    const { left, top, width, height } = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - left) / width) * 100;
    const y = ((event.clientY - top) / height) * 100;

    event.currentTarget.style.setProperty("--zoom-x", `${x}%`);
    event.currentTarget.style.setProperty("--zoom-y", `${y}%`);
  };

  const getProductStyleImages = () => {
    return productImages.map((image) => (
      <div
        key={image.key}
        className={`product-image-tile ${zoomedImage === image.key ? "zoomed" : ""}`}
        onMouseMove={handleProductImageMouseMove}
        onClick={() => setZoomedImage(zoomedImage === image.key ? null : image.key)}
      >
        <img src={image.url} alt={image.alt || "product image"} />
      </div>
    ));
  };


  const handleMobileTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleMobileTouchEnd = (event) => {
    if (touchStartX.current === null || productImages.length < 2) return;

    const touchEndX = event.changedTouches[0]?.clientX;
    if (typeof touchEndX !== "number") return;

    const distance = touchStartX.current - touchEndX;
    touchStartX.current = null;

    if (Math.abs(distance) < 50) return;

    if (distance > 0) {
      setActiveImageIndex((current) => Math.min(current + 1, productImages.length - 1));
    } else {
      setActiveImageIndex((current) => Math.max(current - 1, 0));
    }
  };

  const activeProductImage = productImages[activeImageIndex] || productImages[0];

  return (
    <>
      <PageTemplate className="no-header">
        <div className="content-page">
          <div className="body columns product-page">
            <div className="left-column">
              <div className="product-images desktop-product-images">{getProductStyleImages()}</div>

              {!!activeProductImage && (
                <div className="mobile-product-slider">
                  <div
                    className="mobile-product-slide"
                    onTouchStart={handleMobileTouchStart}
                    onTouchEnd={handleMobileTouchEnd}
                  >
                    <img src={activeProductImage.url} alt={activeProductImage.alt || "product image"} />
                  </div>

                  <div className="mobile-product-slider-dots" aria-label="Product images">
                    {productImages.map((image, index) => (
                      <button
                        key={image.key || `${image.type}-${index}`}
                        type="button"
                        className={`mobile-product-slider-dot ${index === activeImageIndex ? "active" : ""}`}
                        onClick={() => setActiveImageIndex(index)}
                        aria-label={`View product image ${index + 1}`}
                        aria-current={index === activeImageIndex ? "true" : undefined}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="right-column">
              <div className="product-option-box">
                <div className="product-title">{catalogProduct.productName}</div>
                <div className="product-style-code">Style Code: {catalogProduct.styleCode}</div>

                <div className={`product-cost ${isDiscounted ? "discounted" : ""}`}>
                  {getDisplayPrice(selectedOption.unitAmount, selectedOption)}
                </div>

                <div className="option-selector">
                  <div className="option-label">
                    color
                    <strong className="option-selected">{selectedOption.color}</strong>
                  </div>

                  <div className="product-color-options">
                    {styleOptions.map((styleOption) => (
                      <button
                        key={styleOption.lookupKey}
                        onClick={() => chooseColor(styleOption)}
                        className={`product-color-button ${styleOption.lookupKey === selectedOption?.lookupKey ? "selected" : "unselected"}`}
                        style={{ backgroundColor: styleOption.hexCode }}
                        aria-label={styleOption.color}
                        title={styleOption.color}
                        type="button"
                      />
                    ))}
                  </div>
                </div>

                <div className="option-selector">
                  <div className="option-label">
                    size
                    {selectedSize && <strong className="option-selected">{getSizeLabel(selectedSize)}</strong>}
                  </div>

                  <div className="product-size-options">
                    {sizeOptions.map((option) => {
                      const isSelected = option.size === selectedSize;
                      const isOutOfStock = !option.inStock;

                      return (
                        <button
                          key={option.size}
                          onClick={() => chooseSize(option.size)}
                          className={`product-size-option ${isSelected ? "selected" : ""} ${
                            option.size === "XXL" ? "xxl" : ""
                          } ${isOutOfStock ? "out-of-stock" : ""}`}
                          type="button"
                        >
                          {option.size}
                        </button>
                      );
                    })}
                  </div>

                  <button type="button" className="product-size-chart-link" onClick={() => setShowSizeChartModal(true)}>
                    Size Guide
                  </button>
                </div>

                <div className="product-purchase-row">
                  <div className="product-quantity-box">
                    <select
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                      className="product-quantity-select"
                      disabled={!selectedSize || selectedSizeStock === 0 || outOfStock}
                    >
                      {quantityOptions.map((qty) => (
                        <option key={qty} value={qty}>
                          {qty}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Button
                    onClick={handleAddToBag}
                    disabled={disableAddToBag}
                    className={`product-add-to-bag ${!selectedSize || isSoldOut || isMaxInBag ? "disabled" : "enabled"}`}
                    type="button"
                  >
                    {addToBagLabel}
                  </Button>
                </div>

                <div className="description">
                  <div className="right-bar-label">Description</div>
                  <p>{catalogProduct?.details?.description}</p>
                </div>

                <div className="details">
                  <div className="right-bar-label">Details</div>

                  {!!catalogProduct?.details?.bulletPoints?.length && (
                    <ul className="detail-list">
                      {catalogProduct.details.bulletPoints
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((bullet, index) => (
                          <li key={`${bullet.text}-${index}`}>{bullet.text}</li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageTemplate>
      <Modal size="xl" fullscreen={"md-down"} show={showSizeChartModal} animation={false} onHide={() => setShowSizeChartModal(false)}>
        <Wrapper className="modal-styles">
          <Modal.Header closeButton>
            <Modal.Title>Size Guide - {catalogProduct.productName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PageTemplate className="size-guide-modal">
              <SizeChart product={catalogProduct} />
            </PageTemplate>
          </Modal.Body>
        </Wrapper>
      </Modal>
    </>
  );
};

export default Product;
